import React, { useEffect, useRef, useState } from "react";
import { aiApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, Plus, MessageCircle, Wand2, ClipboardCheck, Layers, CalendarClock, Scale, TrendingDown, TrendingUp, UserPlus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import QuickAddDialog from "@/components/UniversalQuickAdd";

const STARTERS = [
  "What needs my attention today?",
  "What is overdue?",
  "What meetings are coming up this week?",
  "Summarize progress across all entities.",
  "Which projects have budget concerns?",
  "Who needs a follow-up?",
];

export default function ExecutiveAssistantPage() {
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickPrefill, setQuickPrefill] = useState("");
  const [quickSection, setQuickSection] = useState("global");

  // "Create in HQ" chips shown under every assistant reply. Tapping one opens
  // the existing Universal Quick Add with the assistant message pre-filled and
  // the section hint biased toward the right record type. No separate action
  // pipeline — everything still flows: reply → Quick Add draft → editable
  // preview → confirm → canonical record.
  const ACTION_CHIPS = [
    { section: "tasks",    label: "Task",     icon: ClipboardCheck,  color: "#A78BFA" },
    { section: "projects", label: "Project",  icon: Layers,          color: "#22D3EE" },
    { section: "calendar", label: "Event",    icon: CalendarClock,   color: "#3B82F6" },
    { section: "notes",    label: "Note",     icon: Scale,           color: "#F59E0B" },
    { section: "budget",   label: "Expense",  icon: TrendingDown,    color: "#EF4444" },
    { section: "budget",   label: "Revenue",  icon: TrendingUp,      color: "#10B981", extraPrompt: "This should be recorded as revenue." },
    { section: "contacts", label: "Contact",  icon: UserPlus,        color: "#94A3B8" },
  ];

  const startAction = (message, chip) => {
    const prefill = chip.extraPrompt ? `${message}\n\n${chip.extraPrompt}` : message;
    setQuickPrefill(prefill);
    setQuickSection(chip.section);
    setQuickOpen(true);
  };

  const loadSessions = async () => {
    try { const r = await aiApi.sessions(); setSessions(r.data); } catch {}
  };
  useEffect(() => { loadSessions(); }, []);

  const loadSession = async (id) => {
    setSessionId(id);
    const r = await aiApi.history(id);
    setMessages(r.data);
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setInput(""); setLoading(true);
    setMessages(m => [...m, { role: "user", content: msg }]);
    try {
      const r = await aiApi.chat(msg, sessionId);
      setSessionId(r.data.session_id);
      setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
      loadSessions();
    } catch(e) {
      toast.error(e.response?.data?.detail || "Assistant is unavailable");
      setMessages(m => m.slice(0, -1));
    } finally { setLoading(false); setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50); }
  };

  return (
    <div className="h-full flex" data-testid="assistant-page">
      <aside className="hidden md:flex w-72 flex-shrink-0 border-r border-white/10 flex-col">
        <div className="p-4 border-b border-white/10">
          <Button onClick={newChat} className="w-full rounded-full bg-white text-black hover:bg-white/90" data-testid="new-chat-btn"><Plus className="w-4 h-4 mr-2"/>New conversation</Button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          <div className="text-eyebrow px-3 py-2">Recent</div>
          {sessions.length === 0 && <div className="text-xs text-white/40 px-3 py-2">No conversations yet.</div>}
          {sessions.map(s => (
            <button key={s.session_id} onClick={() => loadSession(s.session_id)} className={`w-full text-left p-3 rounded-md hover:bg-white/5 ${sessionId === s.session_id ? "bg-white/10" : ""}`}>
              <div className="flex items-start gap-2">
                <MessageCircle className="w-3.5 h-3.5 mt-0.5 text-white/40 flex-shrink-0"/>
                <div className="text-xs text-white/80 line-clamp-2">{s.preview || "(no preview)"}</div>
              </div>
            </button>
          ))}
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <div className="h-16 border-b border-white/10 flex items-center px-4 md:px-6 gap-3">
          <MessageSquare className="w-4 h-4 text-white/60"/>
          <div>
            <div className="font-display font-semibold">Executive Assistant</div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">Read-only access to your HQ data</div>
          </div>
          <button onClick={newChat} className="md:hidden ml-auto text-xs px-3 py-1.5 rounded-full bg-white/10 text-white/80 hover:bg-white/15" data-testid="new-chat-btn-mobile">+ New</button>
          <button onClick={() => { setQuickPrefill(""); setQuickSection("global"); setQuickOpen(true); }} className="hidden md:inline-flex items-center gap-1.5 ml-auto text-xs px-3 py-1.5 rounded-full bg-white/10 text-white/80 hover:bg-white/15" data-testid="assistant-quick-add-btn">
            <Wand2 className="w-3.5 h-3.5"/> Quick add task
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6">
          {messages.length === 0 && (
            <div className="max-w-2xl mx-auto text-center py-10 md:py-16">
              <div className="w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-6 h-6 text-white/80"/>
              </div>
              <h2 className="font-display text-xl md:text-2xl font-semibold mb-2">Ask your Executive Assistant</h2>
              <p className="text-white/50 text-sm mb-6 px-2">Ask about entities, projects, tasks, contacts, calendar, budgets, notes, and decisions.</p>
              <div className="grid md:grid-cols-2 gap-2 max-w-xl mx-auto">
                {STARTERS.map(s => (
                  <button key={s} onClick={() => send(s)} className="text-left p-3 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-sm text-white/80" data-testid={`starter-${s.slice(0,10)}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                <div className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold ${m.role === "user" ? "bg-white text-black" : "bg-white/10 text-white"}`}>
                  {m.role === "user" ? "You" : <MessageSquare className="w-3.5 h-3.5"/>}
                </div>
                <div className={`flex flex-col gap-2 max-w-[85%] ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div className={`p-4 rounded-2xl whitespace-pre-wrap text-sm leading-relaxed ${m.role === "user" ? "bg-white text-black" : "bg-[#151A23] border border-white/10 text-white/90 w-full"}`}>
                    {m.content}
                  </div>
                  {m.role === "assistant" && m.content && (
                    <div className="flex flex-wrap items-center gap-1.5 pl-1" data-testid={`assistant-actions-${i}`}>
                      <span className="text-[10px] uppercase tracking-wider text-white/40 mr-1 flex items-center gap-1">
                        <Sparkles className="w-3 h-3"/> Create in HQ
                      </span>
                      {ACTION_CHIPS.map((chip) => {
                        const Icon = chip.icon;
                        return (
                          <button
                            key={chip.label}
                            onClick={() => startAction(m.content, chip)}
                            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/75 hover:text-white transition-colors"
                            data-testid={`assistant-action-${chip.label.toLowerCase()}-${i}`}
                            title={`Create a ${chip.label} from this reply`}
                          >
                            <Icon className="w-3 h-3" style={{ color: chip.color }} />
                            {chip.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full flex-shrink-0 bg-white/10 flex items-center justify-center"><MessageSquare className="w-3.5 h-3.5 animate-pulse"/></div>
                <div className="p-4 rounded-2xl bg-[#151A23] border border-white/10 text-white/50 text-sm">Working on it…</div>
              </div>
            )}
            <div ref={endRef}/>
          </div>
        </div>

        <div className="border-t border-white/10 p-3 md:p-4" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="max-w-3xl mx-auto flex items-center gap-2 md:gap-3">
            <Input
              value={input}
              onChange={e=>setInput(e.target.value)}
              placeholder="Ask about tasks, meetings, budgets, decisions…"
              className="flex-1 h-11 bg-white/5 border-white/10"
              disabled={loading}
              data-testid="assistant-input"
            />
            <Button type="submit" disabled={loading || !input.trim()} className="rounded-full bg-white text-black hover:bg-white/90 h-11 px-5" data-testid="assistant-send-btn"><Send className="w-4 h-4"/></Button>
          </form>
        </div>
      </div>
      <QuickAddDialog
        open={quickOpen}
        setOpen={setQuickOpen}
        section={quickSection}
        prefillPrompt={quickPrefill}
        onCreated={(created) => {
          setQuickOpen(false);
          const c = Array.isArray(created) ? created[0] : null;
          const rec = c?.record;
          const kind = c?.type;
          if (rec && kind) {
            const label = kind === "expenditure" ? (rec.kind === "revenue" ? "Revenue" : "Expense") : kind.charAt(0).toUpperCase() + kind.slice(1);
            toast.success(`${label} created in HQ`, {
              description: rec.name || rec.title || rec.description || rec.subject || "",
              action: rec.entity_id
                ? { label: "Open", onClick: () => { window.location.href = `/entity/${rec.entity_id}`; } }
                : undefined,
            });
          } else {
            toast.success("Created in HQ");
          }
        }}
      />
    </div>
  );
}
